import Device from './Device'
import Logger from './Log'
import { playerDims } from './utils'

const Log = Logger.for('Outstream')

export const PLAYER_SRC = 'https://serving.stat-rock.com/player/smartrtb.js'

/**
 * Plays a given prebid bid with a vastXml property
 * @param bid prebid bid object { vastXml: ..., type: 'vast' / 'ima', bidder: 'who' }
 * @param slot AdSlot
 * @param content object of RSS feed Item { link, media, title }
 * @returns {Promise<boolean>} promise which resolves with true if cleared
 * or false if empty vast tag
 */
export async function playVideo (bid, slot, media, content) {
  const dims = playerDims(media, bid)

  Log.info('Running local ' + (content ? 'in' : 'out') + 'stream player for ' +
     bid.bidder + ' at ' + dims[0] + 'x' + dims[1])

  const width = dims[0]
  let maxw = 50
  const rect = slot.targetNode().getBoundingClientRect()
  if (rect.width > maxw) {
    maxw = rect.width
    Log.debug('Found targetNode width')
  } else {
    let pr = slot.targetNode().parentElement.getBoundingClientRect()
    if (pr.width > maxw) {
      Log.debug('Checking targetNode parent width')
      maxw = pr.width
    } else {
      pr = slot.targetNode().parentElement.parentElement.getBoundingClientRect()
      if (pr.width > maxw) {
        Log.debug('Using targetNode parents parent width')
        maxw = pr.width
      } else {
        Log.warn('Couldnt find proper player width from parent divs, using static player width')
        maxw = width
      }
    }
  }

  if (maxw > Device.width()) {
    Log.debug('Div larger than screen width! Using screen size')
    maxw = Device.width() // just in case above parent check fails
  }

  const pw = Math.min(dims[0], maxw)
  Log.debug('Player width is ' + pw)

  const div = document.createElement('div')
  div.style.width = pw + 'px'
  div.style['max-width'] = pw + 'px'
  slot.node().appendChild(div)

  const client = bid.type && bid.type.length ? bid.type : 'vast'

  /* global AdPlayerPro */
  const player = AdPlayerPro(div).setup({
    type: content ? 'inStream' : 'inPage',
    aspectratio: dims[0] + ':' + dims[1], // w:h
    width: '100%',
    file: content ? content.media : null,
    autoStart: true,
    soundByHover: true,
    showPercent: 1,
    playerPercent: 1,
    muted: true,
    advertising: {
      adBreakSchedule: content ? { types: ['PRE'] } : null,
      tag: bid.vastUrl ? bid.vastUrl : bid.vastXml,
      client: client.toLowerCase() === 'ima' ? 'googima' : 'vast'
    }
  })

  let loaded = false

  player.on('AdVideoComplete', () => {
    Log.debug('Event: AdVideoComplete')
    if (!content) slot.destroy()
  })

  player.on('AdLoaded', () => {
    Log.debug('Event: AdLoaded')
    loaded = true
  })

  player.on('AdError', (m, e) => {
    Log.debug('Event: AdError ' + m)
    console.log(e)
  })

  return new Promise((resolve, reject) => {
    const to = setTimeout(() => {
      if (!loaded) {
        Log.debug('VAST from ' + bid.bidder + ' failed to clear')
        slot.resetNode() // clean out player
        resolve(false)
      }
    }, 3000)

    player.on('AdVideoStart', () => {
      clearTimeout(to)
      resolve(true)
    })
  })
}
